import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Modal, ModalBody } from "reactstrap";

const ContractFormModal = ({
  isOpen,
  toggle,
  formData,
  setFormData,
  fetchContractData,
  token,
  API,
}) => {
  const { t } = useTranslation();

  const isCreate = !formData._id;

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateEmails = (value) => {
    const emails = String(value || "")
      .split(/[,;]+/)
      .map((email) => email.trim())
      .filter(Boolean);
    if (!emails.length) return false;
    return emails.every(isValidEmail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.contract_number?.trim() ||
      !formData.collector?.trim() ||
      !formData.collectorEmail?.trim()
    ) {
      toast.error(t("All fields are required"));
      return;
    }

    if (!validateEmails(formData.collectorEmail)) {
      toast.error(t("Invalid email address"));
      return;
    }

    const url = formData._id
      ? `${API}contract/update/${formData._id}`
      : `${API}settings/update`;

    const payload = {
      contractNumber: formData.contract_number.trim(),
      contractName: formData.name?.trim() || "",
      contractCollector: formData.collector.trim(),
      collectorEmail: formData.collectorEmail.trim(),
    };

    try {
      const response = await axios.post(url, payload, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        fetchContractData();
        toggle();
        toast.success(
          isCreate
            ? t("The collection email has been successfully created")
            : t("settingsContacts.updatedSuccess"),
          { className: "custom-toast" },
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t("Error saving producer"));
    }
  };

  const updateField = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="settings-contact-modal"
      contentClassName="rounded-lg"
      centered
    >
      <ModalBody>
        <header className="settings-contact-modal__header">
          <h2 className="settings-contact-modal__title">
            {isCreate
              ? t("settingsContacts.addTitle")
              : t("settingsContacts.editTitle")}
          </h2>
          <button
            type="button"
            className="settings-contact-modal__close"
            onClick={toggle}
            aria-label={t("Cancel")}
          >
            ×
          </button>
        </header>

        <form id="contract-form" onSubmit={handleSubmit}>
          <div className="settings-contact-modal__field">
            <label
              htmlFor="contract_number"
              className="settings-contact-modal__label"
            >
              {t("Collection identification reference")}
              <span className="settings-contact-modal__required">*</span>
            </label>
            <input
              id="contract_number"
              name="contract_number"
              type="text"
              className="settings-contact-modal__input"
              value={formData.contract_number || ""}
              onChange={updateField}
            />
          </div>

          <div className="settings-contact-modal__field">
            <label
              htmlFor="collector"
              className="settings-contact-modal__label"
            >
              {t("Collector name")}
              <span className="settings-contact-modal__required">*</span>
            </label>
            <input
              id="collector"
              name="collector"
              type="text"
              className="settings-contact-modal__input"
              value={formData.collector || ""}
              onChange={updateField}
            />
          </div>

          <div className="settings-contact-modal__field">
            <label
              htmlFor="collectorEmail"
              className="settings-contact-modal__label"
            >
              {t("Collection planning email")}
              <span className="settings-contact-modal__required">*</span>
            </label>
            <input
              id="collectorEmail"
              name="collectorEmail"
              type="text"
              className="settings-contact-modal__input"
              value={formData.collectorEmail || ""}
              onChange={updateField}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="settings-contact-modal__footer">
            <button
              type="button"
              className="modal-button modal-button-cancel"
              onClick={toggle}
            >
              {t("Cancel")}
            </button>
            <button type="submit" className="modal-button modal-button-save">
              {isCreate ? t("settingsContacts.addButton") : t("Save")}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ContractFormModal;
